import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  viewChild,
} from '@angular/core';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {AdxBookingResponse} from '../../model/adx-booking-response';
import {AdxBookingService} from '../../service/adx-booking.service';
import {SearchPaginatedAdxBookingResponse} from '../../model/search-paginated-adx-booking-response';
import {AdxBookingDetailsModalComponent} from '../adx-booking-details-modal/adx-booking-details-modal.component';
import {MatDialog} from '@angular/material/dialog';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';
import {VccViewerComponent, VccViewerType} from 'src/app/virtual-credit-card/component/viewer/vcc-viewer.component';
import {VccViewerData} from 'src/app/virtual-credit-card/model/vcc-viewer-data';
import {MatSnackBar} from '@angular/material/snack-bar';
import {HistoryComponent} from 'src/app/history/components/history/history.component';
import {ModalService} from 'src/app/shared/service/modal/modal.service';
import {
  ConfirmationDialogDeleteAdxBookingComponent
} from '../confirmation-dialog-delete-adx-booking/confirmation-dialog-delete-adx-booking.component';
import {PaymentFormComponent} from 'src/app/payments/components/payment-form/payment-form.component';
import {GridEventOutput, GridModel} from "../../../shared/model/page-grid";
import {CreateServiceDialogComponent} from "../create-service-dialog/create-service-dialog.component";
import {PageGridComponent} from "../../../shared/components/page-grid/page-grid.component";
import {AdxBookingGridService} from "../../service/adx-booking-grid.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
    selector: 'adx-booking-list',
    templateUrl: './adx-booking-list.component.html',
    styleUrls: ['./adx-booking-list.component.scss'],
    standalone: false
})
@AutoUnsubscribe()
export class AdxBookingListComponent implements OnInit {
  @Input() public data: any;

  private readonly pageGrid = viewChild(PageGridComponent);

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly azureAuthService = inject(AzureAuthService);
  private readonly adxBookingGridService = inject(AdxBookingGridService);
  private readonly adxBookingService = inject(AdxBookingService);
  private readonly modal = inject(ModalService);

  protected readonly loggedInUserHasDirectorOrFinanceOrManagerRole = computed(() => (this.azureAuthService.checkUserAzureTokenHasManagerRole() && this.azureAuthService.checkUserAzureTokenHasFinanceRole() && this.azureAuthService.checkUserAzureTokenHasDirectorRole()));

  protected readonly gridConfig: GridModel<SearchPaginatedAdxBookingResponse> = {
    gridDataTestId: 'adx-booking-list',
    gridTitle: 'Bookings',
    replayButton: true,
    isTotalsRowHidden: true,
    showActionColumn: true,
    eventButton: this.loggedInUserHasDirectorOrFinanceOrManagerRole() ? { text: 'Add', icon: 'add' } : undefined,
    defaultSort: { active: 'dateUpdated', direction: 'desc' },
    columns: [
      {
        columnProperty: 'id',
        columnHeader: 'Booking Number',
        sort: true,
        clickable: true,
        linkEventFunction: (item: AdxBookingResponse) => `AdxBookings/${item.id}`,
      },
      {
        // @ts-ignore
        columnProperty: 'submitToName' || 'submitTo',
        columnHeader: 'Submit To',
        sort: true,
      },
      {
        columnProperty: 'travelType.travelTypeName',
        columnPropertyFunction: (item: AdxBookingResponse) => item.travelType?.travelTypeName || '',
        columnHeader: 'Travel Type',
        sort: true,
      },
      {
        columnProperty: 'externalTravelServiceType.name',
        columnPropertyFunction: (item: AdxBookingResponse) => item.externalTravelServiceType?.name || '-',
        columnHeader: 'External Travel Type',
        sort: true,
      },
      {
        columnProperty: 'confirmationNumber',
        columnHeader: 'Confirmation Number',
        sort: true,
      },
      {
        columnProperty: 'totalFare',
        columnHeader: 'Total Fare',
        sort: true,
      },
      {
        columnProperty: 'commission',
        columnHeader: 'Commission',
        sort: true,
      },
      {
        columnProperty: 'vendor.vendorName',
        columnPropertyFunction: (item: AdxBookingResponse) => item.vendor?.vendorName || '',
        columnHeader: 'Vendor',
        sort: true,
      },
      {
        columnProperty: 'clientStatus',
        columnPropertyFunction: (item: AdxBookingResponse) => item.clientStatus || item.clientPayStatusName || 'N/A',
        columnHeader: 'Client Status',
        sort: true,
      },
      {
        columnProperty: 'vendorStatus',
        columnPropertyFunction: (item: AdxBookingResponse) => item.vendorStatus || item.vendorPayStatusName || 'N/A',
        columnHeader: 'Vendor Status',
        sort: true,
      },
      {
        columnProperty: 'firstAgent',
        columnHeader: '1st Agent',
        sort: true,
      },
    ],
    filters: [],
    gridGetService: this.adxBookingGridService
  };

  public ngOnInit(): void {
    if (this.data == undefined) {
      this.data = {
        adxInvoiceId: null,
      }
    }

    this.gridConfig.filtersInitial = {
      adxInvoiceId: this.data.adxInvoiceId || null,
    }
  }

  protected handleItemClick({column, item}: GridEventOutput<AdxBookingResponse>): void {
    if (column === 'id') {
      if (this.data.adxInvoiceId == null) {
        window.open(`AdxBookings/${item.id}`);
      } else {
        this.openDialog(item.id, `Booking Information`);
      }
    }
  }

  public openDialog(adxBookingId: number, title: string): void {
    const dialog = this.dialog.open(AdxBookingDetailsModalComponent, {
      width: '65%',
      data: {adxBookingId: adxBookingId, title: title}
    });

    dialog.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(saved => {
        if (!saved) return;
        this.pageGrid()?.getItems();
      });
  }

  protected manageServiceVirtualCreditCardClick(adxBookingId: number) {
    this.dialog.open(VccViewerComponent, {
      width: '60%',
      data: {
        id: adxBookingId,
        type: this.loggedInUserHasDirectorOrFinanceOrManagerRole() ? VccViewerType.Update : VccViewerType.Info,
      } as VccViewerData
    });
  }

  protected handleHistoryClick(adxBookingId: number) {
    this.adxBookingService.getBookingHistoryById(adxBookingId)
      .subscribe((data: any) => {
        if (data.length == 0) {
          this.snackBar.open('No Booking history found for Id: ' + adxBookingId, 'Dismiss', {
            horizontalPosition: 'center',
            verticalPosition: 'bottom',
            panelClass: ['message-position', 'app-notification-success']
          });
        } else {
          this.dialog.open(HistoryComponent, {
            width: '60%',
            data: {
              mode: 'Booking',
              historyData: data,
              id: adxBookingId,
            }
          });
        }
      });
  }

  protected handleDeleteService(adxBooking: AdxBookingResponse) {
    if (adxBooking) {
      const modalRef = this.modal.open(ConfirmationDialogDeleteAdxBookingComponent, {
        data: {adxBookingId: adxBooking.id, recordLocator: adxBooking.recordLocator},
      });
      modalRef.afterClosed().subscribe((deleted) => {
        if (deleted) {
          this.pageGrid()?.getItems();
        }
      });
    }
  }

  protected handleServicePayments(adxBooking: AdxBookingResponse) {
    if (adxBooking) {
      this.dialog.open(PaymentFormComponent, {
        data: {
          adxBookingId: adxBooking.id,
          recordLocator: adxBooking.recordLocator,
          adxInvoiceId: this.gridConfig.filtersInitial.adxInvoiceId,
          dataSourceId: adxBooking.dataSourceId,
        },
      });
    }
  }

  protected handleCreateDialog() {
    const dialog = this.dialog.open(CreateServiceDialogComponent, {
      width: '55%',
      data: {...this.data}
    });

    dialog.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(saved => {
        if (!saved) return;
        this.pageGrid()?.getItems();
      });
  }
}
